import { useEffect, useRef, useState, useCallback } from "react";
import { useToolsUiStore } from "../stores/useToolsUiStore";

/* ------------------------------------------------------------------ */
/* GEOMETRY HELPERS                                                   */
/* ------------------------------------------------------------------ */

const R = 6371008.8;
const toRad = (d) => (d * Math.PI) / 180;

function haversine([lng1, lat1], [lng2, lat2]) {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function lineDistance(coords) {
  let d = 0;
  for (let i = 1; i < coords.length; i++) d += haversine(coords[i - 1], coords[i]);
  return d;
}

function ringArea(ring) {
  if (ring.length < 3) return 0;
  let total = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const [lng1, lat1] = ring[i];
    const [lng2, lat2] = ring[i + 1];
    total += toRad(lng2 - lng1) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
  }
  return Math.abs((total * R * R) / 2);
}

// Area-weighted polygon centroid (planar approximation in lng/lat space).
// Good enough for measurement-tool scales — the curvature error is tiny
// compared to label-readability tolerance.
function polygonCentroid(ring) {
  if (ring.length < 4) {
    const [sx, sy] = ring.reduce(
      ([ax, ay], [x, y]) => [ax + x, ay + y],
      [0, 0]
    );
    return [sx / ring.length, sy / ring.length];
  }

  let cx = 0, cy = 0, signedArea = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const [x0, y0] = ring[i];
    const [x1, y1] = ring[i + 1];
    const cross = x0 * y1 - x1 * y0;
    signedArea += cross;
    cx += (x0 + x1) * cross;
    cy += (y0 + y1) * cross;
  }
  signedArea *= 0.5;

  if (Math.abs(signedArea) < 1e-12) {
    let sx = 0, sy = 0;
    for (let i = 0; i < ring.length - 1; i++) {
      sx += ring[i][0];
      sy += ring[i][1];
    }
    const n = ring.length - 1;
    return [sx / n, sy / n];
  }

  return [cx / (6 * signedArea), cy / (6 * signedArea)];
}

/* ------------------------------------------------------------------ */
/* FORMATTERS — imperial                                              */
/* ------------------------------------------------------------------ */

export function formatDistance(meters) {
  if (meters == null) return "—";
  const ft = meters * 3.28084;
  if (ft < 1000) return `${ft.toFixed(0)} ft`;
  const mi = meters / 1609.344;
  return `${mi.toFixed(2)} mi`;
}

export function formatArea(sqMeters) {
  if (sqMeters == null) return "—";
  const acres = sqMeters / 4046.8564224;
  if (acres < 1) return `${acres.toFixed(3)} ac`;
  if (acres < 640) return `${acres.toFixed(2)} ac`;
  const sqMi = sqMeters / 2_589_988.110336;
  return `${sqMi.toFixed(2)} mi²`;
}

/* ------------------------------------------------------------------ */
/* LIVE STATS — pure derivation                                       */
/* ------------------------------------------------------------------ */

// In-progress shape is an OPEN polyline → running multi-segment distance
// (placed verts plus the cursor as the tentative next vertex). Area is derived
// only when the user closes the ring (at finalize), so adding a 3rd+ vertex
// extends the distance instead of flipping the tool to area.
function computeLiveStats(coords, cursor) {
  if (coords.length === 0) return null;
  const path = cursor ? [...coords, cursor] : coords;
  if (path.length < 2) return null;
  return { kind: "line", distance: lineDistance(path) };
}

/* ------------------------------------------------------------------ */
/* CONSTANTS                                                          */
/* ------------------------------------------------------------------ */

const SNAP_PIXELS = 10;
const DEDUP_METERS = 0.5;

const SRC_ID = "measure-src";
const GHOST_SRC_ID = "measure-ghost-src";
const LABEL_SRC_ID = "measure-label-src";
const FILL_LAYER = "measure-fill";
const LINE_HALO_LAYER = "measure-line-halo";
const LINE_LAYER = "measure-line";
const POINT_LAYER = "measure-points";
const GHOST_LAYER = "measure-ghost";
const LABEL_LAYER = "measure-label";

/* ------------------------------------------------------------------ */
/* LAYER ENSURE                                                       */
/* ------------------------------------------------------------------ */

function ensureMeasureLayers(map) {
  if (!map || !map.getStyle) return false;

  if (!map.getSource(SRC_ID)) {
    map.addSource(SRC_ID, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
  }
  if (!map.getSource(GHOST_SRC_ID)) {
    map.addSource(GHOST_SRC_ID, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
  }

  if (!map.getLayer(FILL_LAYER)) {
    map.addLayer({
      id: FILL_LAYER,
      type: "fill",
      source: SRC_ID,
      filter: ["==", ["geometry-type"], "Polygon"],
      paint: {
        "fill-color": "#16f2d4",
        "fill-opacity": 0.15,
      },
    });
  }

  // Halo — sits BENEATH the colored line so the colored line reads on any
  // basemap. Added before LINE_LAYER so MapLibre renders it lower in the stack.
  if (!map.getLayer(LINE_HALO_LAYER)) {
    map.addLayer({
      id: LINE_HALO_LAYER,
      type: "line",
      source: SRC_ID,
      filter: ["in", ["geometry-type"], ["literal", ["LineString", "Polygon"]]],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": "#ffffff",
        "line-width": 4.5,
        "line-opacity": 0.65,
        "line-blur": 0.5,
      },
    });
  }

  if (!map.getLayer(LINE_LAYER)) {
    map.addLayer({
      id: LINE_LAYER,
      type: "line",
      source: SRC_ID,
      filter: ["in", ["geometry-type"], ["literal", ["LineString", "Polygon"]]],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": "#16f2d4",
        "line-width": 2.5,
        "line-dasharray": [2, 1.5],
      },
    });
  }

  if (!map.getLayer(GHOST_LAYER)) {
    map.addLayer({
      id: GHOST_LAYER,
      type: "line",
      source: GHOST_SRC_ID,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": "#16f2d4",
        "line-width": 1.5,
        "line-opacity": 0.6,
      },
    });
  }

  if (!map.getLayer(POINT_LAYER)) {
    map.addLayer({
      id: POINT_LAYER,
      type: "circle",
      source: SRC_ID,
      filter: ["==", ["geometry-type"], "Point"],
      paint: {
        "circle-radius": 5,
        "circle-color": "#16f2d4",
        "circle-stroke-color": "#0b1220",
        "circle-stroke-width": 2,
      },
    });
  }

  if (!map.getSource(LABEL_SRC_ID)) {
    map.addSource(LABEL_SRC_ID, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
  }

  if (!map.getLayer(LABEL_LAYER)) {
    map.addLayer({
      id: LABEL_LAYER,
      type: "symbol",
      source: LABEL_SRC_ID,
      layout: {
        "text-field": ["get", "label"],
        "text-size": 12,
        "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
        "text-anchor": ["get", "anchor"],
        "text-offset": ["get", "offset"],
        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "text-padding": 0,
      },
      paint: {
        "text-color": "#ffffff",
        "text-halo-color": "#0b1220",
        "text-halo-width": 1.5,
        "text-halo-blur": 0.3,
      },
    });
  }

  return true;
}

/* ------------------------------------------------------------------ */
/* HOOK                                                               */
/* ------------------------------------------------------------------ */

export function useMeasure({ map, mapLoaded }) {
  const [active, setActive] = useState(false);
  const [vertexCount, setVertexCount] = useState(0);
  const [liveStats, setLiveStats] = useState(null);
  const [result, setResult] = useState(null);

  const activeRef = useRef(false);
  const coordsRef = useRef([]);
  const cursorRef = useRef(null);
  const resultRef = useRef(null);

  const renderRef = useRef(null);
  const renderGhostRef = useRef(null);
  const renderLabelRef = useRef(null);

  /* ensure layers + survive style reload */
  useEffect(() => {
    if (!map || !mapLoaded) return;
    ensureMeasureLayers(map);
    const onStyleLoad = () => {
      ensureMeasureLayers(map);
      renderRef.current?.();
      renderGhostRef.current?.();
      renderLabelRef.current?.();
    };
    map.on("style.load", onStyleLoad);
    return () => map.off("style.load", onStyleLoad);
  }, [map, mapLoaded]);

  /* render committed/in-progress geometry */
  const render = useCallback(() => {
    if (!map) return;
    ensureMeasureLayers(map);
    const src = map.getSource(SRC_ID);
    if (!src) return;

    const coords = coordsRef.current;
    const r = resultRef.current;
    const features = [];

    coords.forEach((c) =>
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: c },
        properties: {},
      })
    );

    if (r?.kind === "area") {
      features.push({
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [[...coords, coords[0]]] },
        properties: {},
      });
    } else if (r?.kind === "line") {
      features.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: coords },
        properties: {},
      });
    } else if (!r && coords.length >= 2) {
      // In-progress preview: an OPEN polyline over the placed verts (the cursor
      // leg is drawn by the ghost layer). N verts → multi-segment distance; it
      // only becomes a polygon once finalized as an area (close-the-ring).
      features.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: coords },
        properties: {},
      });
    }

    src.setData({ type: "FeatureCollection", features });
  }, [map]);

  /* render rubber band */
  const renderGhost = useCallback(() => {
    if (!map) return;
    ensureMeasureLayers(map);
    const src = map.getSource(GHOST_SRC_ID);
    if (!src) return;

    const coords = coordsRef.current;
    const cur = cursorRef.current;

    if (
      !activeRef.current ||
      resultRef.current ||
      coords.length === 0 ||
      !cur
    ) {
      src.setData({ type: "FeatureCollection", features: [] });
      return;
    }

    // Rubber-band only the open leg from the last placed vertex to the cursor.
    // No closing edge: the in-progress shape is an open path (distance) — it
    // becomes an area only when the user clicks back on the first vertex.
    const last = coords[coords.length - 1];
    src.setData({
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: { type: "LineString", coordinates: [last, cur] },
          properties: {},
        },
      ],
    });
  }, [map]);

  /* render labels — live stat on the map alongside the slide-out pill */
  const renderLabel = useCallback(() => {
    if (!map) return;
    ensureMeasureLayers(map);
    const src = map.getSource(LABEL_SRC_ID);
    if (!src) return;

    const coords = coordsRef.current;
    const cur = cursorRef.current;
    const r = resultRef.current;

    const features = [];

    if (r?.kind === "line") {
      features.push({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: coords[coords.length - 1],
        },
        properties: {
          label: formatDistance(r.distance),
          anchor: "top",
          offset: [0, 0.8],
        },
      });
    } else if (r?.kind === "area") {
      const ring = [...coords, coords[0]];
      const [cx, cy] = polygonCentroid(ring);
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [cx, cy] },
        properties: {
          label: formatArea(r.area),
          anchor: "center",
          offset: [0, 0],
        },
      });
    } else if (!r && activeRef.current) {
      // Running open-path distance, labeled at the leading end (the cursor if
      // we have one, else the last placed vertex).
      const path = cur ? [...coords, cur] : coords;
      if (path.length >= 2) {
        features.push({
          type: "Feature",
          geometry: { type: "Point", coordinates: path[path.length - 1] },
          properties: {
            label: formatDistance(lineDistance(path)),
            anchor: "top",
            offset: [0, 0.8],
          },
        });
      }
    }

    src.setData({ type: "FeatureCollection", features });
  }, [map]);

  useEffect(() => { renderRef.current = render; }, [render]);
  useEffect(() => { renderGhostRef.current = renderGhost; }, [renderGhost]);
  useEffect(() => { renderLabelRef.current = renderLabel; }, [renderLabel]);
  useEffect(() => { resultRef.current = result; }, [result]);

  // Mirror "currently capturing clicks" into the store — gates other map
  // click handlers in MapView. Finalized state leaves it null so the user
  // can interact normally until they clear or exit.
  useEffect(() => {
    useToolsUiStore.getState().setMeasureMode(
      active && !result ? "measure" : null
    );
  }, [active, result]);

  // Ensure the store mirror doesn't outlive the hook.
  useEffect(() => () => useToolsUiStore.getState().setMeasureMode(null), []);

  /* ------------------------------------------------------------------ */
  /* PUBLIC API                                                         */
  /* ------------------------------------------------------------------ */

  const start = useCallback(() => {
    if (activeRef.current) return;
    activeRef.current = true;
    coordsRef.current = [];
    cursorRef.current = null;
    resultRef.current = null;
    setActive(true);
    setVertexCount(0);
    setLiveStats(null);
    setResult(null);
    render();
    renderGhost();
    renderLabel();
    if (map) map.getCanvas().style.cursor = "crosshair";
  }, [map, render, renderGhost, renderLabel]);

  const exit = useCallback(() => {
    activeRef.current = false;
    coordsRef.current = [];
    cursorRef.current = null;
    resultRef.current = null;
    setActive(false);
    setVertexCount(0);
    setLiveStats(null);
    setResult(null);
    if (!map) return;
    ensureMeasureLayers(map);
    map.getSource(SRC_ID)?.setData({ type: "FeatureCollection", features: [] });
    map.getSource(GHOST_SRC_ID)?.setData({ type: "FeatureCollection", features: [] });
    map.getSource(LABEL_SRC_ID)?.setData({ type: "FeatureCollection", features: [] });
    map.getCanvas().style.cursor = "";
  }, [map]);

  const clear = useCallback(() => {
    if (!activeRef.current) return;
    coordsRef.current = [];
    cursorRef.current = null;
    resultRef.current = null;
    setVertexCount(0);
    setLiveStats(null);
    setResult(null);
    if (!map) return;
    map.getSource(SRC_ID)?.setData({ type: "FeatureCollection", features: [] });
    map.getSource(GHOST_SRC_ID)?.setData({ type: "FeatureCollection", features: [] });
    map.getSource(LABEL_SRC_ID)?.setData({ type: "FeatureCollection", features: [] });
    // Crosshair + active state preserved — user can start a new measurement.
  }, [map]);

  const finalizeLine = useCallback(() => {
    const coords = coordsRef.current;
    if (coords.length < 2) return;
    const r = {
      kind: "line",
      distance: lineDistance(coords), // full multi-segment length (all N-1 legs)
      coords: [...coords],
    };
    resultRef.current = r;
    setResult(r);
    setLiveStats(null);
    // Cursor stays crosshair through finalize — only `exit` restores it.
    render();
    renderGhost();
    renderLabel();
  }, [render, renderGhost, renderLabel]);

  const finalizeArea = useCallback(() => {
    const coords = coordsRef.current;
    if (coords.length < 3) return;
    const ring = [...coords, coords[0]];
    const r = {
      kind: "area",
      area: ringArea(ring),
      perimeter: lineDistance(ring),
      coords: [...coords],
    };
    resultRef.current = r;
    setResult(r);
    setLiveStats(null);
    // Cursor stays crosshair through finalize — only `exit` restores it.
    render();
    renderGhost();
    renderLabel();
  }, [render, renderGhost, renderLabel]);

  /* ------------------------------------------------------------------ */
  /* MAP HANDLERS                                                       */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!map || !mapLoaded) return;

    const onClick = (e) => {
      if (!activeRef.current) return;
      if (resultRef.current) return;          // finalized — wait for clear
      e.originalEvent._measureHandled = true;

      // Snap-close on click near the first vertex (requires 3+ placed).
      if (coordsRef.current.length >= 3) {
        const v1px = map.project(coordsRef.current[0]);
        const dx = v1px.x - e.point.x;
        const dy = v1px.y - e.point.y;
        if (Math.sqrt(dx * dx + dy * dy) <= SNAP_PIXELS) {
          finalizeArea();
          return;
        }
      }

      const pt = [e.lngLat.lng, e.lngLat.lat];
      coordsRef.current = [...coordsRef.current, pt];
      cursorRef.current = pt;
      setVertexCount(coordsRef.current.length);
      setLiveStats(computeLiveStats(coordsRef.current, cursorRef.current));
      render();
      renderGhost();
      renderLabel();
    };

    const onMouseMove = (e) => {
      if (!activeRef.current) return;
      // Re-assert crosshair on every move — MapLibre's drag/hover behavior
      // otherwise overrides the canvas cursor (e.g., reverts to "grab" after
      // a dblclick). Keeping it sticky for the whole measure session,
      // finalized state included, until exit().
      const canvas = map.getCanvas();
      if (canvas.style.cursor !== "crosshair") {
        canvas.style.cursor = "crosshair";
      }
      cursorRef.current = [e.lngLat.lng, e.lngLat.lat];
      if (!resultRef.current) {
        setLiveStats(computeLiveStats(coordsRef.current, cursorRef.current));
        renderGhost();
        renderLabel();
      }
    };

    const onMouseOut = () => {
      if (!activeRef.current) return;
      cursorRef.current = null;
      if (!resultRef.current) {
        setLiveStats(computeLiveStats(coordsRef.current, cursorRef.current));
        renderGhost();
        renderLabel();
      }
    };

    const onDblClick = (e) => {
      if (!activeRef.current || resultRef.current) return;
      e.preventDefault();      // suppress map's dblclick-zoom (preserved)

      // MapLibre fires the click of the 2nd dblclick tap before this handler,
      // so a duplicate vertex was just appended at the same lngLat. Drop it
      // before deciding line-vs-area.
      let coords = coordsRef.current;
      if (coords.length >= 2) {
        const pen = coords[coords.length - 2];
        const last = coords[coords.length - 1];
        if (haversine(pen, last) < DEDUP_METERS) {
          coords = coords.slice(0, -1);
          coordsRef.current = coords;
          setVertexCount(coords.length);
          setLiveStats(computeLiveStats(coords, cursorRef.current));
        }
      }

      if (coords.length < 2) return;
      // Open path → distance (any N≥2). An area is made by closing the ring
      // (clicking the first vertex), handled in onClick's snap-close.
      finalizeLine();
    };

    const onContextMenu = (e) => {
      if (!activeRef.current || resultRef.current) return;
      e.preventDefault();
      if (coordsRef.current.length < 2) return;
      finalizeLine(); // open path → distance; close the ring to get an area
    };

    map.on("click", onClick);
    map.on("mousemove", onMouseMove);
    map.on("mouseout", onMouseOut);
    map.on("dblclick", onDblClick);
    map.on("contextmenu", onContextMenu);

    return () => {
      map.off("click", onClick);
      map.off("mousemove", onMouseMove);
      map.off("mouseout", onMouseOut);
      map.off("dblclick", onDblClick);
      map.off("contextmenu", onContextMenu);
    };
  }, [
    map, mapLoaded,
    render, renderGhost, renderLabel,
    finalizeLine, finalizeArea,
  ]);

  return { active, vertexCount, liveStats, result, start, exit, clear };
}
